/**
 * The assistant's tools — the port of the ten in `const TOOLS = { … }` at source
 * lines 3966–4017 of `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`.
 *
 * ## What a tool is here
 *
 * A tool is a named, described, schema-carrying async function over the OEM+
 * data pack. It is the unit both modes of the demo shared: live mode handed
 * the ten `{name, description, inputSchema}` triples to Claude and executed
 * whatever it called; local mode (`./local-answer.ts`) called the same
 * `execute` functions directly from regex-routed branches. This port ships
 * local mode, but the definitions stay tool-shaped so the Tier 3 live row is a
 * wiring job — see `./rules.ts`.
 *
 * ## Why every tool is async, when the demo's were not
 *
 * The demo held the whole 6.67 MB `B` object in a module global. This port
 * splits it into an eager core (`../../data/core.ts`) and twelve lazy domain
 * files behind `loadOemDomain()`. Tools run **outside React** — they are
 * called from `ChatContext.send`, not from a render — so they cannot use
 * `useOemData()`. They `await loadOemDomain(...)` instead, which hits the same
 * module-level promise cache the hook uses: the first question pays the fetch,
 * every later one is resolved from cache.
 *
 * ## Where the arithmetic lives
 *
 * Nowhere new. Every derivation a tool needs — `statusOf`, `healthOf`,
 * `wosOf`, `slope`, the condition bands, the cost rollups — is imported from
 * `../oem-engine.ts` or `../../schema/`. A tool's own body is shaping and
 * filtering only: it assembles the result object the demo's prompt and
 * branches expect, in the demo's key order, with the demo's rounding.
 *
 * ## Two demo bugs, fixed, and marked
 *
 * 1. `fleet_overview` returned `needs_attention` but `localAnswer`'s catch-all
 *    branch (demo line 4081) read `m.flagged_critical` / `m.flagged_watch`,
 *    which were never emitted — the default branch threw a `TypeError` on
 *    every question that reached it. The two keys are emitted here alongside
 *    `needs_attention`. Marked `// demo bug`.
 * 2. `machine_history` returned `condition` on each flagged component, but the
 *    machine-history branch (demo line 4072) read `c.health` and printed
 *    `condition undefined/100`. Fixed at the consumer; the tool keeps the
 *    demo's key.
 *
 * Nothing else was changed: the magic numbers (the 0.55/0.45/0.25 risk ladder,
 * the 240 °C and 85 °C limits, the eight-row `top()` cut, the twelve-machine
 * `predict_risk` slice) are the original's.
 *
 * ## fork(oem v1.8) — filters and four more tools
 *
 * The intent router passes the qualifiers the regex engine dropped:
 * `stock_check` takes `mine` (that site store and its DC) and `below_min`;
 * `predict_risk` takes `cls` / `subclass`; `breakdown_trend` takes a comma
 * list of classes and a `component` keyword. Every new input is optional
 * and falsy by default, so an old call — and its trace entry — is unchanged.
 * Four tools are added after the demo's ten: `part_network`, `pm_due`,
 * `alert_confidence` and `job_cost`. `TOOL_MANIFEST` follows `TOOLS`.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no DOM, no clock, no randomness.
 *
 * @see ./router.ts — the intent router whose intents call these
 * @see ./rules.ts — the system prompt describing them to a live model
 * @see docs/logic/workspace/chat-rail.md — the OEM+ local engine section
 */

import {
  BY_ID,
  CLASSES,
  COST_PER_MACHINE,
  ENERGY_FACTORS,
  FLEET,
  IDS,
  MINES,
  MINE_ORDER,
  RATES,
  HEALTH,
  hasMachine,
  type ComponentHealth,
} from '../../data/core.ts';
import {
  ATTENTION_BANDS,
  CONDITION_BAND_LABEL,
  CONDITION_BAND_ORDER,
  type ConditionBand,
  type MineCode,
} from '../../schema/machine.ts';
import type { EmailHeader, LibraryDocument, MeetingRecord } from '../../schema/document.ts';
import type { CataloguePart } from '../../schema/part.ts';
import type { ServiceCase } from '../../schema/service.ts';
import type {
  DailyMetricRow,
  EventDailyRow,
  LiveSample,
  MachineEnergy,
  ShiftDailyRow,
} from '../../schema/telemetry.ts';
import type { WorkOrder } from '../../schema/work-order.ts';
import type {
  ReportDownload,
  ReportFormat,
  ReportKind,
  ReportRef,
  ReportStep,
  WeeklyReportPack,
} from '../../schema/report.ts';
import { unpackTable } from '../columnar.ts';
import {
  coi,
  EXCHANGE,
  fleetCostPerOpHourByMine,
  fmt$,
  healthBand,
  healthOf,
  inWk,
  mineWeek,
  PART_OF_COMP,
  pmDue,
  statusOf,
  WK,
  wosOf,
  type ExchangeUnit,
} from '../oem-engine.ts';
import {
  isWeeklyKind,
  REPORT_DOWNLOAD,
  REPORT_FORMAT,
  REPORT_LABEL,
  REPORT_TITLE,
  reportPath,
  reportRef,
  WEEKLY_DIVISION,
} from '../report-catalog.ts';
import { ALERTS } from '../seed/alerts.ts';
import { INCIDENTS } from '../seed/incidents.ts';
import { loadOemDomain } from '../useOemData.ts';
import { fixed, slope, topCounts } from './stats.ts';

// ===========================================================================
// Tool protocol
// ===========================================================================

/** A JSON-Schema fragment, as the demo wrote them — shape only, no validation. */
export interface ToolInputSchema {
  readonly type: 'object';
  readonly properties: Readonly<Record<string, { type: string; enum?: readonly string[]; description?: string }>>;
  readonly required?: readonly string[];
}

/** Whatever a tool returns. Narrowed per tool by the interfaces below. */
export type ToolResult = Record<string, unknown>;

/**
 * What a tool knows about the turn it is serving.
 *
 * `trace` is the port of the demo's module-level `toolLog` (source line 4018):
 * a mutable list of `"name(arg, arg)"` strings, in call order, rendered as the
 * chat message's "Ran … → …" line. It is owned by the caller and passed in, so
 * nothing in this module holds state between turns.
 */
export interface OemToolContext {
  /** The "VIEWING AS" persona; carried for the live-mode prompt. */
  readonly personaId: string;
  /** The route the question was asked from. */
  readonly pathname: string;
  /** Appended to, in call order, by {@link runTool}. */
  readonly trace: string[];
}

/** One tool. `I` is its input shape, `R` its result shape. */
export interface OemTool<I = Record<string, never>, R = ToolResult> {
  readonly name: string;
  readonly description: string;
  readonly inputSchema?: ToolInputSchema;
  execute(input: I, ctx: OemToolContext): Promise<R>;
}

/**
 * Demo line 4018. `logTool(n, i)` — the trace entry for one call, built from
 * the truthy argument values only, exactly as the demo did.
 */
export function traceEntry(name: string, input?: Record<string, unknown>): string {
  const args = input ? Object.values(input).filter(Boolean) : [];
  return args.length ? `${name}(${args.join(', ')})` : name;
}

/** Run a tool, recording the call on `ctx.trace`. The demo's `toolList` wrapper. */
export async function runTool<I, R>(
  tool: OemTool<I, R>,
  input: I,
  ctx: OemToolContext,
): Promise<R> {
  ctx.trace.push(traceEntry(tool.name, input as Record<string, unknown>));
  return tool.execute(input, ctx);
}

/** A fresh, empty tool context for one chat turn. */
export const newToolContext = (personaId: string, pathname: string): OemToolContext => ({
  personaId,
  pathname,
  trace: [],
});

// ===========================================================================
// Shared loaders
// ===========================================================================

/** `useOemData('work-orders')`, unpacked, for code that cannot use the hook. */
export async function loadWorkOrders(): Promise<WorkOrder[]> {
  const d = await loadOemDomain('work-orders');
  return unpackTable<WorkOrder>(d.wos);
}

/** `useOemData('telemetry')`, with the three columnar tables unpacked. */
export async function loadTelemetry(): Promise<{
  dailyMetric: DailyMetricRow[];
  shiftDaily: ShiftDailyRow[];
  eventsDaily: EventDailyRow[];
  energy: readonly MachineEnergy[];
}> {
  const d = await loadOemDomain('telemetry');
  return {
    dailyMetric: unpackTable<DailyMetricRow>(d.daily_metric),
    shiftDaily: unpackTable<ShiftDailyRow>(d.shift_daily),
    eventsDaily: unpackTable<EventDailyRow>(d.events_daily),
    energy: d.energy,
  };
}

const upper = (v: unknown): string => String(v ?? '').toUpperCase().trim();

/** Resolve a machine id, or throw the demo's message. */
function requireMachine(machineId: unknown, hint = true): string {
  const id = upper(machineId);
  if (!hasMachine(id)) {
    throw new Error(
      hint
        ? `Unknown machine ${id}. Valid IDs look like J-04, L-02, T-09, P-03.`
        : `Unknown machine ${id}`,
    );
  }
  return id;
}

/** `'jumbo,longhole'` → a set of class keys. fork(oem v1.8). */
function classSet(cls: unknown): Set<string> {
  return new Set(
    String(cls)
      .toLowerCase()
      .split(/\s*,\s*/)
      .filter(Boolean),
  );
}

// ===========================================================================
// 1 · fleet_overview
// ===========================================================================

/** One site's row in {@link FleetOverviewResult}. */
export interface FleetOverviewMine {
  mine: MineCode;
  name: string;
  state: string;
  depth_m: number;
  ground: string;
  operator: string;
  quality: string;
  /** `'<mode> (<fms>)'` — the demo's concatenation. */
  data_mode: string;
  labour_rate: number;
  machines: number;
  condition_bands: Record<ConditionBand, number>;
  /** `'L-02 (Very poor)'` strings. */
  needs_attention: string[];
  /**
   * `// demo bug` — emitted here but not in the demo, where the catch-all
   * `localAnswer` branch read them and threw. Major + very poor.
   */
  flagged_critical: string[];
  /** `// demo bug` — see {@link FleetOverviewMine.flagged_critical}. Poor. */
  flagged_watch: string[];
  avg_cost_per_op_hour: number;
}

export interface FleetOverviewResult extends ToolResult {
  mines: FleetOverviewMine[];
  classes: Record<string, string>;
  open_alerts: string[];
  story_machines: Readonly<Record<string, string>>;
}

const BAND_KEYS = ['good', 'fair', 'poor', 'vpoor', 'major'] as const;

/** Demo line 3967. */
export const fleetOverview: OemTool<Record<string, never>, FleetOverviewResult> = {
  name: 'fleet_overview',
  description:
    'Fleet-wide summary: the three mines (ground, operator model, data mode, labour rate), machine counts per division, machines in the major-issue and poor condition bands, and cost per operating hour by mine. Use first for any broad or comparative question.',
  async execute() {
    const mines = MINE_ORDER.map((m): FleetOverviewMine => {
      const at = FLEET.filter((f) => f.mine === m);
      const attn = at.filter((f) => ATTENTION_BANDS.includes(statusOf(f.machine_id)));
      return {
        mine: m,
        name: MINES[m].name,
        state: MINES[m].state,
        depth_m: MINES[m].depth_m,
        ground: MINES[m].ground,
        operator: MINES[m].operator_model,
        quality: MINES[m].operator_quality,
        data_mode: `${MINES[m].data_mode} (${MINES[m].fms})`,
        labour_rate: MINES[m].labour_rate,
        machines: at.length,
        condition_bands: Object.fromEntries(
          BAND_KEYS.map((k) => [k, at.filter((f) => statusOf(f.machine_id) === k).length]),
        ) as Record<ConditionBand, number>,
        needs_attention: attn.map(
          (f) => `${f.machine_id} (${CONDITION_BAND_LABEL[statusOf(f.machine_id)]})`,
        ),
        // demo bug — see the interface note.
        flagged_critical: at
          .filter((f) => ['major', 'vpoor'].includes(statusOf(f.machine_id)))
          .map((f) => f.machine_id),
        flagged_watch: at.filter((f) => statusOf(f.machine_id) === 'poor').map((f) => f.machine_id),
        avg_cost_per_op_hour: fixed(
          COST_PER_MACHINE.filter((c) => BY_ID[c.machine_id].mine === m).reduce(
            (a, c) => a + c.cost_per_op_hour_aud,
            0,
          ) / Math.max(1, at.length),
          0,
        ),
      };
    });

    return {
      mines,
      classes: Object.fromEntries(Object.entries(CLASSES).map(([k, v]) => [k, v.name])),
      open_alerts: ALERTS.map((a) => `${a.id} ${a.machine} ${a.comp}: ${a.title} (${a.conf})`),
      story_machines: IDS,
    };
  },
};

// ===========================================================================
// 2 · machine_history
// ===========================================================================

export interface MachineHistoryWork {
  wo: string;
  date: string;
  type: string;
  status: string;
  component: string;
  cause: string | null;
  desc: string;
  root_cause: string | null;
  downtime_h: number | null;
  cost: number;
  crew: string;
  parts: string | null;
}

export interface MachineHistoryFlagged {
  component: string;
  /** The demo's key. `local-answer.ts` reads this, not `health` — see the header. */
  condition: number;
  band: string;
  hours_since_change: number;
  /** `null` on components with nothing recorded. */
  note: string | null;
}

export interface MachineHistoryResult extends ToolResult {
  machine: string;
  model: string;
  class_name: string;
  division: string;
  mine: string;
  mine_code: MineCode;
  engine_hours: number;
  commissioned: string;
  tele_remote: boolean;
  data_mode: string;
  status: string;
  condition_bands: string;
  flagged_components: MachineHistoryFlagged[];
  lowest_component: { component: string; condition: number; band: string };
  breakdowns: number;
  correctives: number;
  pms: number;
  pms_late: number;
  total_cost_18m: number;
  cost_per_op_hour: number;
  model_avg_cost_per_op_hour: number;
  recent_work: MachineHistoryWork[];
  cases: string[];
  alerts: string[];
}

/** Demo line 3968. */
export const machineHistory: OemTool<{ machine_id: string }, MachineHistoryResult> = {
  name: 'machine_history',
  description:
    'Full history for one machine: identity, mine, hours, condition band, components in the poor/major-issue bands with scores, last 20 non-preventive work orders (date, type, component, cause, downtime, cost, crew), breakdown count, PM lateness, cost per operating hour vs peers, open cases and alerts. Input: {machine_id} e.g. "L-02".',
  inputSchema: { type: 'object', properties: { machine_id: { type: 'string' } }, required: ['machine_id'] },
  async execute({ machine_id }) {
    const id = requireMachine(machine_id);
    const f = BY_ID[id];
    const [workOrders, service] = await Promise.all([loadWorkOrders(), loadOemDomain('service')]);
    const ws = wosOf(id, { workOrders });
    const cp = COST_PER_MACHINE.find((c) => c.machine_id === id);
    const peers = COST_PER_MACHINE.filter((c) => BY_ID[c.machine_id].model === f.model);
    const health = healthOf(id);
    const worst = health.slice().sort((a, b) => a.health_score - b.health_score)[0];

    return {
      machine: id,
      model: f.model,
      class_name: CLASSES[f.model].name,
      division: f.division,
      mine: MINES[f.mine].name,
      mine_code: f.mine,
      engine_hours: f.engine_hours,
      commissioned: f.commissioned,
      tele_remote: f.remote,
      data_mode: MINES[f.mine].data_mode,
      status: CONDITION_BAND_LABEL[statusOf(id)],
      condition_bands: '80-100 good, 60-79 fair, 40-59 poor, 20-39 very poor, <20 major issue',
      flagged_components: health
        .filter((h) => ATTENTION_BANDS.includes(healthBand(h)))
        .map((h) => ({
          component: h.component,
          condition: h.health_score,
          band: CONDITION_BAND_LABEL[healthBand(h)],
          hours_since_change: h.hours_since_change,
          note: h.note,
        })),
      lowest_component: {
        component: worst.component,
        condition: worst.health_score,
        band: CONDITION_BAND_LABEL[healthBand(worst)],
      },
      breakdowns: ws.filter((w) => w.wo_type === 'Breakdown').length,
      correctives: ws.filter((w) => w.wo_type === 'Corrective').length,
      pms: ws.filter((w) => w.wo_type === 'Preventive').length,
      pms_late: ws.filter((w) => w.status.includes('late')).length,
      total_cost_18m: Math.round(cp?.amount_aud ?? 0),
      cost_per_op_hour: cp?.cost_per_op_hour_aud ?? 0,
      model_avg_cost_per_op_hour: fixed(
        peers.reduce((a, c) => a + c.cost_per_op_hour_aud, 0) / Math.max(1, peers.length),
        0,
      ),
      recent_work: ws
        .filter((w) => w.wo_type !== 'Preventive')
        .slice(0, 20)
        .map((w) => ({
          wo: w.wo_id,
          date: w.created.slice(0, 10),
          type: w.wo_type,
          status: w.status,
          component: w.component,
          cause: w.failure_cause,
          desc: w.description,
          root_cause: w.root_cause,
          downtime_h: w.downtime_h,
          cost: w.total_direct_cost_aud + (w.downtime_cost_aud || 0),
          crew: w.crew,
          parts: w.parts_used,
        })),
      cases: service.cases
        .filter((c: ServiceCase) => c.machine_id === id)
        .map((c: ServiceCase) => `${c.case_no} ${c.type}: ${c.subject} [${c.status}]`),
      alerts: ALERTS.filter((a) => a.machine === id).map(
        (a) => `${a.id}: ${a.title} — ${a.window} (conf ${a.conf})`,
      ),
    };
  },
};

// ===========================================================================
// 3 · breakdown_trend
// ===========================================================================

export interface BreakdownMonth {
  month: string;
  breakdowns: number;
  correctives: number;
  downtime_h: number;
  cost: number;
}

export interface BreakdownMineRow {
  mine: MineCode;
  name: string;
  events: number;
  breakdowns: number;
  mean_downtime_h: number;
  pm_late_share: number;
  operator_abuse_events: number;
}

export interface BreakdownTrendInput {
  mine?: MineCode | string | null;
  model?: string | null;
  /** One class, or a comma list (`'jumbo,longhole,sdrill'`) — fork(oem v1.8). */
  cls?: string | null;
  machine_id?: string | null;
  /**
   * fork(oem v1.8): a component keyword ("coupling", "turbo"), matched against
   * the work order's component, description and root cause.
   */
  component?: string | null;
}

export interface BreakdownTrendResult extends ToolResult {
  filter: BreakdownTrendInput;
  events: number;
  by_month: BreakdownMonth[];
  trend_breakdowns_per_month_slope: number;
  last3_vs_first3_breakdowns: [number, number];
  top_components: string[];
  top_causes: string[];
  by_mine: BreakdownMineRow[];
  coupling_history: string[];
}

/** Demo line 3970. */
export const breakdownTrend: OemTool<BreakdownTrendInput, BreakdownTrendResult> = {
  name: 'breakdown_trend',
  description:
    'Breakdown and corrective work-order trend over 18 months, grouped by month, with top failing components and causes, mean downtime, and reactive vs preventive mix. Filter by mine code (KG/BR/MI/OH/GD/MR), model (e.g. ML-21), machine class (jumbo/longhole/lhd/truck/sdrill, or a comma list), machine_id or a component keyword (e.g. "coupling"). Use for "trend", "why do we keep breaking down", "which components fail most", "how many X failures", mine comparisons.',
  inputSchema: {
    type: 'object',
    properties: {
      mine: { type: 'string' },
      model: { type: 'string' },
      cls: { type: 'string' },
      machine_id: { type: 'string' },
      component: { type: 'string' },
    },
  },
  async execute({ mine, model, cls, machine_id, component } = {}) {
    const all = await loadWorkOrders();
    let ws = all.filter((w) => w.wo_type !== 'Preventive');
    if (mine) ws = ws.filter((w) => w.mine === upper(mine));
    if (model) ws = ws.filter((w) => w.model === upper(model));
    if (cls) {
      const set = classSet(cls);
      ws = ws.filter((w) => set.has(BY_ID[w.machine_id].cls));
    }
    if (machine_id) ws = ws.filter((w) => w.machine_id === upper(machine_id));
    if (component) {
      const k = String(component).toLowerCase();
      ws = ws.filter((w) =>
        `${w.component} ${w.description} ${w.root_cause ?? ''}`.toLowerCase().includes(k),
      );
    }

    const bym: Record<string, BreakdownMonth> = {};
    ws.forEach((w) => {
      const m = w.created.slice(0, 7);
      bym[m] = bym[m] ?? { month: m, breakdowns: 0, correctives: 0, downtime_h: 0, cost: 0 };
      if (w.wo_type === 'Breakdown') bym[m].breakdowns += 1;
      else bym[m].correctives += 1;
      bym[m].downtime_h += w.downtime_h || 0;
      bym[m].cost += w.total_direct_cost_aud + (w.downtime_cost_aud || 0);
    });

    const byMine = MINE_ORDER.map((m): BreakdownMineRow => {
      const x = ws.filter((w) => w.mine === m);
      const closedPms = all.filter(
        (w) => w.mine === m && w.wo_type === 'Preventive' && w.status.startsWith('Closed'),
      ).length;
      return {
        mine: m,
        name: MINES[m].name,
        events: x.length,
        breakdowns: x.filter((w) => w.wo_type === 'Breakdown').length,
        mean_downtime_h: fixed(
          x.reduce((a, w) => a + (w.downtime_h || 0), 0) / Math.max(1, x.length),
          1,
        ),
        pm_late_share: fixed(
          all.filter((w) => w.mine === m && w.wo_type === 'Preventive' && w.status.includes('late'))
            .length / Math.max(1, closedPms),
          2,
        ),
        operator_abuse_events: x.filter((w) => w.failure_cause === 'Operator abuse').length,
      };
    });

    const months = Object.values(bym)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((m) => ({ ...m, downtime_h: fixed(m.downtime_h, 1), cost: Math.round(m.cost) }));
    const bd = months.map((m) => m.breakdowns);

    return {
      filter: { mine, model, cls, machine_id, component },
      events: ws.length,
      by_month: months,
      trend_breakdowns_per_month_slope: fixed(slope(bd), 3),
      last3_vs_first3_breakdowns: [
        bd.slice(-3).reduce((a, b) => a + b, 0),
        bd.slice(0, 3).reduce((a, b) => a + b, 0),
      ],
      top_components: topCounts(ws, 'component'),
      top_causes: topCounts(ws, 'failure_cause'),
      by_mine: byMine,
      coupling_history: [
        `${IDS.L22} BR 2025-11 6,950 h end-of-life`,
        `${IDS.L03} KG 2026-02 3,120 h contaminated oil`,
        `${IDS.L14} KG 2026-09 3,640 h contaminated oil + ramming`,
      ],
    };
  },
};

// ===========================================================================
// 4 · fault_trend
// ===========================================================================

export interface FaultTrendResult extends ToolResult {
  machine: string;
  data_mode: string;
  metric: string | undefined;
  limit: number | null;
  series: { date: string; value: number; utilisation_pct: number }[];
  slope_per_day: number;
  change_over_window: number;
  /** `delays` is free text in the source ("2 × tramway", ""), not a count. */
  alarms_by_day: { date: string; alarms: number; abuse: number; delays: string }[];
  alarm_codes: Record<string, number>;
  live_sample: {
    samples: number;
    exceedances: number;
    max_brake_temp: number;
    derates: number;
  } | null;
}

/** Demo line 3972. The class key metric limits: trucks 240 °C, LHDs 85 °C. */
const METRIC_LIMIT: Readonly<Record<string, number | null>> = {
  truck: 240,
  lhd: 85,
  jumbo: null,
  longhole: null,
};

const isTruckSample = (s: LiveSample): s is Extract<LiveSample, { brake_temp_rr_c: number }> =>
  'brake_temp_rr_c' in s;
const isLoaderSample = (s: LiveSample): s is Extract<LiveSample, { engine_derate_pct: number }> =>
  'engine_derate_pct' in s;

/** Demo line 3972. */
export const faultTrend: OemTool<{ machine_id: string }, FaultTrendResult> = {
  name: 'fault_trend',
  description:
    'Telemetry trend for one machine over the last 12 days: the class key metric (percussion pressure for drills, hydraulic oil temperature for LHDs, rear brake temperature for trucks) daily means with slope and limit, alarm counts and abuse events per day, and the alarm codes seen. Live (Mount Isolde) or shift-end (others). Input {machine_id}.',
  inputSchema: { type: 'object', properties: { machine_id: { type: 'string' } }, required: ['machine_id'] },
  async execute({ machine_id }) {
    const id = requireMachine(machine_id, false);
    const f = BY_ID[id];
    const [tele, liveDomain] = await Promise.all([loadTelemetry(), loadOemDomain('telemetry-live')]);
    const dm = tele.dailyMetric.filter((d) => d.machine_id === id);
    const sd = tele.shiftDaily.filter((s) => s.machine_id === id);
    const codes: Record<string, number> = {};
    tele.eventsDaily
      .filter((e) => e.machine_id === id)
      .forEach((e) => {
        codes[e.code] = (codes[e.code] ?? 0) + e.n;
      });
    const live = liveDomain.live[id] as readonly LiveSample[] | undefined;

    return {
      machine: id,
      data_mode: MINES[f.mine].data_mode,
      metric: dm[0]?.metric,
      limit: METRIC_LIMIT[f.cls] ?? null,
      series: dm.map((d) => ({ date: d.date, value: d.value, utilisation_pct: d.utilisation_pct })),
      slope_per_day: fixed(slope(dm.map((d) => d.value)), 2),
      change_over_window: dm.length ? fixed(dm[dm.length - 1].value - dm[0].value, 1) : 0,
      alarms_by_day: sd.map((s) => ({
        date: s.date,
        alarms: s.alarms,
        abuse: s.abuse,
        delays: s.delays,
      })),
      alarm_codes: codes,
      live_sample: live
        ? {
            samples: live.length,
            exceedances: live.filter(
              (r) =>
                (isTruckSample(r) ? r.brake_temp_rr_c : 0) > 240 ||
                (isLoaderSample(r) ? r.engine_derate_pct : 0) > 0,
            ).length,
            max_brake_temp: Math.max(...live.map((r) => (isTruckSample(r) ? r.brake_temp_rr_c : 0))),
            derates: live.filter((r) => isLoaderSample(r) && r.engine_derate_pct > 0).length,
          }
        : null,
    };
  },
};

// ===========================================================================
// 5 · cost_summary
// ===========================================================================

export type CostGroupBy = 'mine' | 'crew' | 'class' | 'model' | 'machine';

export interface CostSummaryRow extends Record<string, string | number> {
  group: string;
  total: number;
}

export interface CostSummaryResult extends ToolResult {
  group_by: CostGroupBy;
  mine: MineCode | null;
  rows: CostSummaryRow[];
  rate_card: typeof RATES;
  top_cost_per_op_hour: { machine: string; mine: MineCode; model: string; aud_per_op_h: number }[];
  avg_cost_per_op_hour_by_mine: Record<MineCode, number>;
  note: string;
}

/** Demo line 3974. */
export const costSummary: OemTool<
  { group_by?: CostGroupBy; mine?: string | null },
  CostSummaryResult
> = {
  name: 'cost_summary',
  description:
    'Cost analysis from the ledger (18 months, A$): totals by category (labour, parts, freight, external, lost production) grouped by mine, crew, machine class, model or machine; includes rate cards (labour A$/h and lost-production A$/h per class per mine) and top machines by cost per operating hour. Input {group_by: "mine"|"crew"|"class"|"model"|"machine", mine?: "KG"|"BR"|"MI"}.',
  inputSchema: { type: 'object', properties: { group_by: { type: 'string' }, mine: { type: 'string' } } },
  async execute({ group_by = 'mine', mine } = {}) {
    const code = (mine ? upper(mine) : null) as MineCode | null;
    const costs = await loadOemDomain('costs');
    const rows: Record<string, CostSummaryRow> = {};
    const add = (k: string, cat: string, v: number) => {
      rows[k] = rows[k] ?? { group: k, total: 0 };
      rows[k][cat] = ((rows[k][cat] as number) ?? 0) + v;
      rows[k].total += v;
    };

    if (group_by === 'crew') {
      costs.cost_by_crew_cat
        .filter((r) => !code || r.mine === code)
        .forEach((r) => add(`${r.crew} (${r.mine})`, r.category, r.amount_aud));
    } else {
      costs.cost_by_machine_cat
        .filter((r) => !code || BY_ID[r.machine_id].mine === code)
        .forEach((r) => {
          const f = BY_ID[r.machine_id];
          const k =
            ({
              mine: MINES[f.mine].name,
              class: f.cls,
              model: f.model,
              machine: r.machine_id,
            } as Record<string, string>)[group_by] ?? MINES[f.mine].name;
          add(k, r.category, r.amount_aud);
        });
    }

    const out = Object.values(rows)
      .sort((a, b) => b.total - a.total)
      .slice(0, 25)
      .map(
        (r) =>
          Object.fromEntries(
            Object.entries(r).map(([k, v]) => [k, typeof v === 'number' ? Math.round(v) : v]),
          ) as CostSummaryRow,
      );

    return {
      group_by,
      mine: code,
      rows: out,
      rate_card: RATES,
      top_cost_per_op_hour: COST_PER_MACHINE.slice()
        .sort((a, b) => b.cost_per_op_hour_aud - a.cost_per_op_hour_aud)
        .slice(0, 8)
        .map((c) => ({
          machine: c.machine_id,
          mine: BY_ID[c.machine_id].mine,
          model: BY_ID[c.machine_id].model,
          aud_per_op_h: c.cost_per_op_hour_aud,
        })),
      avg_cost_per_op_hour_by_mine: Object.fromEntries(
        MINE_ORDER.map((m) => [
          m,
          fixed(
            COST_PER_MACHINE.filter((c) => BY_ID[c.machine_id].mine === m).reduce(
              (a, c) => a + c.cost_per_op_hour_aud,
              0,
            ) / Math.max(1, FLEET.filter((f) => f.mine === m).length),
            0,
          ),
        ]),
      ) as Record<MineCode, number>,
      note: 'Seismic exclusion downtime at Mount Isolde is coded to ground conditions, not to machines or Redgum (MI-SEIS-002).',
    };
  },
};

// ===========================================================================
// 6 · stock_check
// ===========================================================================

export interface StockRow extends Record<string, unknown> {
  part: string;
  description: string;
  list_price_aud: number;
  lead_time_days: number;
  criticality: string;
  /** `'Kalbarri store: 2 (min 4) BELOW MIN'` strings. */
  stock: string[];
}

/** Input for {@link stockCheck}. */
export interface StockCheckInput {
  part_no?: string | null;
  keyword?: string | null;
  machine_id?: string | null;
  /**
   * fork(oem v1.8): restrict the stock lines to this mine's site store and
   * the distribution centre that serves it.
   */
  mine?: string | null;
  /** fork(oem v1.8): only parts with a line below its minimum (at `mine`'s store, if given). */
  below_min?: boolean | null;
}

/** The locations a mine draws from: its site store, then its serving DC. */
export const mineLocations = (mine: MineCode): string[] => [`${mine} site store`, RATES[mine].dc];

/** Demo line 3976. */
export const stockCheck: OemTool<StockCheckInput, StockRow[]> = {
  name: 'stock_check',
  description:
    'Parts stock across site stores and distribution centres with minimum levels, list price and lead time. Input any of {part_no, keyword (matches description/system), machine_id (parts that fit its class), mine (that site store and its DC only), below_min (only lines below minimum)}.',
  inputSchema: {
    type: 'object',
    properties: {
      part_no: { type: 'string' },
      keyword: { type: 'string' },
      machine_id: { type: 'string' },
      mine: { type: 'string' },
      below_min: { type: 'boolean' },
    },
  },
  async execute({ part_no, keyword, machine_id, mine, below_min } = {}) {
    const parts = await loadOemDomain('parts');
    let ps: readonly CataloguePart[] = parts.parts;
    if (part_no) ps = ps.filter((p) => p.part_no.toUpperCase().includes(upper(part_no)));
    if (keyword) {
      const k = String(keyword).toLowerCase();
      ps = ps.filter((p) => `${p.description} ${p.system}`.toLowerCase().includes(k));
    }
    if (machine_id) {
      const f = BY_ID[upper(machine_id)];
      if (f) ps = ps.filter((p) => p.fits === 'all' || p.fits === f.cls);
    }
    const code = mine ? (upper(mine) as MineCode) : null;
    const places = code && MINES[code] ? mineLocations(code) : null;
    const lineOk = (i: { location: string }) => !places || places.includes(i.location);
    if (below_min) {
      const low = parts.inventory.filter(
        (i) =>
          i.qty_on_hand < i.min_level &&
          (places ? i.location === places[0] : i.location.includes('site')),
      );
      ps = ps.filter((p) => low.some((i) => i.part_no === p.part_no));
    }
    return ps.slice(0, 12).map((p) => ({
      part: p.part_no,
      description: p.description,
      list_price_aud: p.list_price_aud,
      lead_time_days: p.lead_time_days,
      criticality: p.criticality,
      stock: parts.inventory
        .filter((i) => i.part_no === p.part_no && lineOk(i))
        .map(
          (i) =>
            `${i.location}: ${i.qty_on_hand} (min ${i.min_level})${
              i.qty_on_hand < i.min_level ? ' BELOW MIN' : ''
            }`,
        ),
    }));
  },
};

// ===========================================================================
// 7 · energy_summary
// ===========================================================================

export type EnergyGroupBy = 'mine' | 'drive' | 'class' | 'machine';

export interface EnergyRow {
  group: string;
  machines: number;
  hours: number;
  diesel_l: number;
  kwh: number;
  co2_kg: number;
  energy_cost_aud: number;
  kg_co2_per_op_hour: number;
}

export interface EnergySummaryResult extends ToolResult {
  group_by: EnergyGroupBy;
  rows: EnergyRow[];
  factors: typeof ENERGY_FACTORS;
  note: string;
}

/** Demo line 3978. */
export const energySummary: OemTool<
  { group_by?: EnergyGroupBy; mine?: string | null },
  EnergySummaryResult
> = {
  name: 'energy_summary',
  description:
    'Diesel litres, electricity kWh, CO2-e and emissions intensity (kg CO2-e per operating hour) over the last 12 days, grouped by mine, drive type (diesel / battery-electric / diesel tram + electric drill), machine class or machine, with the emission factors used. Input {group_by: "mine"|"drive"|"class"|"machine", mine?}.',
  inputSchema: { type: 'object', properties: { group_by: { type: 'string' }, mine: { type: 'string' } } },
  async execute({ group_by = 'mine', mine } = {}) {
    const code = mine ? upper(mine) : null;
    const { energy } = await loadTelemetry();
    const rows: Record<string, Omit<EnergyRow, 'kg_co2_per_op_hour'>> = {};
    energy
      .filter((e) => !code || e.mine === code)
      .forEach((e) => {
        const f = BY_ID[e.machine_id];
        const k =
          ({
            mine: MINES[e.mine].name,
            drive: e.drive,
            class: f.cls,
            machine: e.machine_id,
          } as Record<string, string>)[group_by] ?? MINES[e.mine].name;
        rows[k] = rows[k] ?? {
          group: k,
          machines: 0,
          hours: 0,
          diesel_l: 0,
          kwh: 0,
          co2_kg: 0,
          energy_cost_aud: 0,
        };
        const r = rows[k];
        r.machines += 1;
        r.hours += e.hours;
        r.diesel_l += e.diesel_l;
        r.kwh += e.energy_kwh;
        r.co2_kg += e.co2_kg;
        r.energy_cost_aud += e.energy_cost_aud;
      });

    return {
      group_by,
      rows: Object.values(rows).map((r) => ({
        ...r,
        hours: Math.round(r.hours),
        diesel_l: Math.round(r.diesel_l),
        kwh: Math.round(r.kwh),
        co2_kg: Math.round(r.co2_kg),
        kg_co2_per_op_hour: fixed(r.co2_kg / Math.max(1, r.hours), 1),
        energy_cost_aud: Math.round(r.energy_cost_aud),
      })),
      factors: ENERGY_FACTORS,
      note: '12-day window 29 Aug – 9 Sep from shift records. Battery-electric loaders: L-07 (Broken Ridge), L-12 (Mount Isolde). Autonomous production rigs: P-03, P-05.',
    };
  },
};

// ===========================================================================
// 8 · generate_report
// ===========================================================================

export type { ReportKind };
/** Re-exported from the catalogue so existing importers keep one name. */
export { REPORT_TITLE };

/**
 * What `generate_report` returns — the demo's `generateReport()` result (demo
 * line 2677) minus the HTML: which report, where it opens, the steps the chat
 * shows while it is "built", and the figures the reply talks from.
 */
export interface ReportBuilt extends ToolResult {
  status: 'built';
  kind: ReportKind;
  mine: MineCode | null;
  title: string;
  subtitle: string;
  /** `RLABEL` — the long label on the generation box. */
  label: string;
  /** The demo's format tag. */
  format: ReportFormat;
  /** What Download produces here, or `null` for in-app only. */
  download: ReportDownload;
  /** Where the report opens — `shared/report-catalog.ts` `reportPath()`. */
  route: string;
  /** Demo 2668. `reportSteps(kind, mine)`. */
  steps: ReportStep[];
  /** Two to four short lines of the figures that matter, for the reply. */
  headline: string[];
}

/** A person's first name, for the email's "addressed to" line. */
const first = (name: string): string => name.split(' ')[0];

/**
 * Demo 2668–2672. `reportSteps(kind, mine)` — the data reads the chat shows,
 * each with the count it found. Counts are real: they are the rows the report
 * page reads.
 */
async function reportSteps(ref: ReportRef): Promise<ReportStep[]> {
  const m = ref.mine;
  const div = isWeeklyKind(ref.kind) ? WEEKLY_DIVISION[ref.kind] : null;
  const ids = FLEET.filter((f) =>
    ref.kind === 'sos' ? !m || f.mine === m : m ? f.mine === m : f.division === div,
  ).map((f) => f.machine_id);
  const idSet = new Set(ids);
  const step = (label: string, detail: string): ReportStep => ({ label, detail });

  if (ref.kind === 'sos') {
    const [workOrders, service, parts, people] = await Promise.all([
      loadWorkOrders(),
      loadOemDomain('service'),
      loadOemDomain('parts'),
      loadOemDomain('people'),
    ]);
    const jobs = service.tapp_jobs;
    return [
      step(
        'Condition register',
        `${HEALTH.filter((h) => idSet.has(h.machine_id) && ATTENTION_BANDS.includes(healthBand(h))).length} flagged components · ${ids.length} machines`,
      ),
      step('Alerts & incidents', `${ALERTS.length} open alerts · ${INCIDENTS.length} incidents this fortnight`),
      step(
        'technician-app jobs',
        `${jobs.filter((j) => j.status !== 'Completed').length} open · ${
          jobs.filter((j) => j.status === 'Completed' && j.off_site && j.off_site >= '2026-09-08').length
        } completed since yesterday`,
      ),
      step(
        'Approvals & parts',
        `${workOrders.filter((w) => w.status.startsWith('Draft')).length} drafts · ${
          parts.orders.filter((o) => /Draft/.test(o.status)).length
        } requisitions · ${
          parts.inventory.filter((i) => i.qty_on_hand < i.min_level && i.location.includes('site')).length
        } store lines below minimum`,
      ),
      step(
        'CSS & technicians',
        `${people.css.rows.length} survey responses · ${
          people.tapp_techs.filter((t) => t.status === 'On site').length
        } technicians on site`,
      ),
      step('Composing', 'S.O.S report → in-app'),
    ];
  }

  const [workOrders, tele] = await Promise.all([loadWorkOrders(), loadTelemetry()]);
  const steps = [
    step('Condition register', `${HEALTH.filter((h) => idSet.has(h.machine_id)).length} components · ${ids.length} machines`),
    step(
      'Shift records',
      `${tele.shiftDaily.filter((x) => idSet.has(x.machine_id) && inWk(x.date)).length} machine-days · ${WK.short}`,
    ),
    step(
      'SAP PM work orders',
      `${workOrders.filter((w) => idSet.has(w.machine_id) && inWk(w.created.slice(0, 10))).length} this week · ${
        workOrders.filter((w) => idSet.has(w.machine_id) && w.status.startsWith('Draft')).length
      } drafts`,
    ),
  ];
  if (ref.kind === 'email' && m) {
    const parts = await loadOemDomain('parts');
    steps.push(
      step(
        'Parts portal',
        `${parts.inventory.filter((i) => i.location === `${m} site store` && i.qty_on_hand < i.min_level).length} lines below minimum`,
      ),
    );
  }
  if (ref.kind === 'deck' && m) {
    const costs = await loadOemDomain('costs');
    steps.push(
      step(
        'Cost ledger',
        `${costs.cost_by_month_mine.filter((c) => c.mine === m).length} month × category rows · 18 months`,
      ),
    );
  }
  const grid = m ? ENERGY_FACTORS.find((x) => x.mine === m)?.grid_kgco2_per_kwh : null;
  steps.push(
    step('Energy', `diesel 2.68 kg/L · grid ${grid != null ? `${grid} kg/kWh` : '3 site factors'}`),
    step(
      'Composing',
      `${REPORT_LABEL[ref.kind]}${m ? ` · ${MINES[m].name}` : ''} → ${(REPORT_DOWNLOAD[ref.kind] ?? 'in-app').toUpperCase()}`,
    ),
  );
  return steps;
}

/** The figures the reply leads with, per kind. Every number is the page's. */
async function reportHeadline(ref: ReportRef): Promise<string[]> {
  if (isWeeklyKind(ref.kind)) {
    const pack = (await loadOemDomain('reports')).reports[ref.kind] as unknown as WeeklyReportPack;
    const blocks = Object.values(pack.by_mine).filter((b) => b != null);
    const avail = blocks.map((b) => b.availability_pct);
    const flagged = blocks
      .flatMap((b) => b.flagged)
      .sort((a, b) => a.worst_score - b.worst_score);
    const worst = flagged[0];
    return [
      `Availability ${Math.min(...avail)}–${Math.max(...avail)}% by mine; ${pack.total_breakdowns} breakdown${pack.total_breakdowns === 1 ? '' : 's'}; ${fmt$(pack.total_cost_aud)} direct + lost-production cost; ${pack.total_co2_t} t CO₂-e.`,
      `${flagged.length} machine${flagged.length === 1 ? '' : 's'} need attention${
        worst ? ` — worst ${worst.machine_id} (${worst.worst_component} ${worst.worst_score}/100)` : ''
      }.`,
      `${pack.pm_due.length} preventives due in the next 14 days; the largest order this week is ${
        pack.top_work_orders[0] ? `${pack.top_work_orders[0].wo_id} on ${pack.top_work_orders[0].machine_id}` : 'none'
      }.`,
    ];
  }

  const m = ref.mine;
  if (!m) {
    // All-sites S.O.S — the counts its first and third sections open on.
    const workOrders = await loadWorkOrders();
    const hot = FLEET.filter((f) => ['vpoor', 'major'].includes(statusOf(f.machine_id)));
    const drafts = workOrders.filter((w) => w.status.startsWith('Draft')).length;
    return [
      `${hot.length} hotspots in the very-poor or major-issue bands across ${MINE_ORDER.length} sites.`,
      `${ALERTS.length} open alerts; ${drafts} drafted work orders waiting on approval.`,
      'Four sections: hotspots, activity, work in progress, evaluation and prompt — one prompt per site lead.',
    ];
  }
  const [workOrders, tele, parts] = await Promise.all([
    loadWorkOrders(),
    loadTelemetry(),
    loadOemDomain('parts'),
  ]);
  const mw = mineWeek(m, { workOrders, shiftDaily: tele.shiftDaily, inventory: parts.inventory });
  const lines = [
    `Availability ${mw.avail}% across ${mw.n} machines; ${mw.breakdowns} breakdown${mw.breakdowns === 1 ? '' : 's'}; ${fmt$(mw.cost)} all-in this week.`,
    mw.flagged.length
      ? `${mw.flagged.length} machine${mw.flagged.length === 1 ? '' : 's'} at risk: ${mw.flagged.map((r) => r.machine_id).join(', ')}.`
      : 'No machine in the poor, very poor or major-issue bands.',
  ];
  if (ref.kind === 'email') {
    const people = await loadOemDomain('people');
    const to = people.people.filter(
      (p) => p.mine === m && /Superintendent|Planner|Field Service Technician/.test(p.role),
    );
    lines.unshift(`Addressed to ${to.map((p) => first(p.name)).join(', ') || 'the site team'} from the Fleet Reliability Lead.`);
    lines.push(
      `${mw.low.length} store line${mw.low.length === 1 ? '' : 's'} below minimum; ${mw.drafts.length} draft${mw.drafts.length === 1 ? '' : 's'} waiting on approval.`,
    );
  }
  if (ref.kind === 'deck') {
    lines.push(
      `A$${fleetCostPerOpHourByMine(m)} per operating hour against ${MINE_ORDER.filter((x) => x !== m)
        .map((x) => `${MINES[x].name} A$${fleetCostPerOpHourByMine(x)}`)
        .join(', ')}.`,
    );
    if (mw.alerts[0]) lines.push(`Open alert: ${mw.alerts[0].machine} ${mw.alerts[0].comp} — ${mw.alerts[0].window}.`);
  }
  if (ref.kind === 'sos') lines.push(`${mw.drafts.length} drafts waiting on approval at the site.`);
  return lines;
}

/**
 * Demo line 3980 / 2677. Builds nothing in the chat itself: it resolves the
 * report's address, counts the rows each generation step reads, and returns
 * the figures the reply leads with. The report page renders the document
 * from the same data, so the chat and the page cannot disagree.
 */
export const generateReport: OemTool<{ kind: ReportKind; mine?: string | null }, ReportBuilt> = {
  name: 'generate_report',
  description:
    'Build one of the standard documents from the live workspace data, open it in the main pane for the user and return its headline figures. kind: "email" = machine risk summary email to a mine\'s technical team; "drills" / "loadhaul" / "surface" = weekly status for that division; "deck" = 8-slide equipment summary presentation for one mine; "sos" = the S.O.S daily report for one mine or all sites.',
  inputSchema: {
    type: 'object',
    properties: {
      kind: { type: 'string', enum: ['email', 'drills', 'loadhaul', 'surface', 'deck', 'sos'] },
      mine: { type: 'string', description: 'KG, BR, MI, OH, GD or MR — for email, deck and sos' },
    },
    required: ['kind'],
  },
  async execute({ kind, mine }) {
    const ref = reportRef(kind, mine);
    const [steps, headline] = await Promise.all([reportSteps(ref), reportHeadline(ref)]);
    const site = ref.mine ? MINES[ref.mine].name : null;
    const subtitle = isWeeklyKind(ref.kind)
      ? `Week ${WK.label}`
      : ref.kind === 'sos'
        ? `Service Operations Summary · Wed 09 Sep 2026 06:00 · ${site ?? 'all sites'}`
        : `${site} · ${WK.short}`;
    return {
      status: 'built',
      kind: ref.kind,
      mine: ref.mine,
      title: `${REPORT_TITLE[ref.kind]}${site && ref.kind !== 'sos' ? ` · ${site}` : ''}`,
      subtitle,
      label: REPORT_LABEL[ref.kind],
      format: REPORT_FORMAT[ref.kind],
      download: REPORT_DOWNLOAD[ref.kind],
      route: reportPath(ref),
      steps,
      headline,
    };
  },
};

// ===========================================================================
// 9 · search_sources
// ===========================================================================

/** One hit — the demo's `{source, snippet}` pair. */
export interface SearchHit extends Record<string, unknown> {
  source: string;
  snippet: string;
}

/** An entry in the assistant's text index. `k` is the demo's kind label. */
interface IndexEntry {
  k: string;
  t: string;
  txt: string;
  l: string;
}

/**
 * Demo lines 2520–2537. The slice of the demo's global `INDEX` this tool
 * searches — documents, emails, meetings, video transcripts, cases, alerts.
 *
 * Note `k: 'Transcript'` on videos: the tool filters on `'Video'`, so video
 * transcripts never reach a result. That is the demo's own behaviour and is
 * kept, so a ported answer cites exactly what the original cited.
 */
async function buildSearchIndex(): Promise<IndexEntry[]> {
  const [docIndex, comms, service] = await Promise.all([
    loadOemDomain('doc-index'),
    loadOemDomain('comms'),
    loadOemDomain('service'),
  ]);
  const idx: IndexEntry[] = [];
  const push = (k: string, t: string, txt: string) => idx.push({ k, t, txt, l: txt.toLowerCase() });

  docIndex.docs.forEach((d: LibraryDocument) =>
    push('Document', d.title.slice(0, 90), `${d.file} ${d.excerpt}`),
  );
  comms.emails.forEach((e: EmailHeader) =>
    push('Email', e.subject, `${e.subject} ${e.from_} ${comms.email_bodies[e.file] ?? ''}`),
  );
  comms.meetings.forEach((m: MeetingRecord) =>
    push('Meeting', m.title, `${m.title} ${m.attendees} ${comms.transcripts[m.transcript] ?? ''}`),
  );
  comms.videos.forEach((v) =>
    push('Transcript', v.title, `${v.title} ${v.equipment} ${comms.vtts[v.transcript] ?? ''}`),
  );
  service.cases.forEach((c: ServiceCase) =>
    push('Case', `${c.case_no} · ${c.type}`, `${c.case_no} ${c.subject} ${c.machine_id} ${c.status}`),
  );
  ALERTS.forEach((a) =>
    push('Alert', `${a.id} · ${a.title}`, `${a.title} ${a.machine} ${a.comp} ${a.why} alert predictive`),
  );
  return idx;
}

/** Demo line 2538. `search(q)` — every term must hit; a title hit scores 3. */
function searchIndex(idx: readonly IndexEntry[], q: string): IndexEntry[] {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  return idx
    .map((i) => {
      let sc = 0;
      for (const t of terms) {
        if (!i.l.includes(t)) return null;
        sc += i.t.toLowerCase().includes(t) ? 3 : 1;
      }
      return { i, sc };
    })
    .filter((x): x is { i: IndexEntry; sc: number } => x != null)
    .sort((a, b) => b.sc - a.sc)
    .slice(0, 40)
    .map((x) => x.i);
}

const SEARCHABLE_KINDS = ['Document', 'Email', 'Meeting', 'Video', 'Case', 'Alert'];

/** Demo line 4013. */
export const searchSources: OemTool<{ query: string }, SearchHit[]> = {
  name: 'search_sources',
  description:
    'Full-text search across documents (procedures, bulletins, reports), emails, meeting transcripts, video transcripts, cases and alerts. Returns up to 6 hits with a short snippet and source name. Use to ground claims and to cite.',
  inputSchema: { type: 'object', properties: { query: { type: 'string' } }, required: ['query'] },
  async execute({ query }) {
    const idx = await buildSearchIndex();
    const res = searchIndex(idx, String(query))
      .filter((i) => SEARCHABLE_KINDS.includes(i.k))
      .slice(0, 6);
    const terms = String(query).toLowerCase().split(/\s+/);
    return res.map((i) => {
      const pos = Math.max(0, i.l.indexOf(terms[0]));
      return {
        source: `${i.k}: ${i.t}`,
        snippet: i.txt.slice(Math.max(0, pos - 120), pos + 260).replace(/\s+/g, ' '),
      };
    });
  },
};

// ===========================================================================
// 10 · predict_risk
// ===========================================================================

export interface RiskRow extends Record<string, unknown> {
  machine: string;
  mine: MineCode;
  model: string;
  /** 0–1, capped at 0.98. */
  risk: number;
  factors: string[];
  recommendation: string;
}

/** Input for {@link predictRisk}. */
export interface PredictRiskInput {
  machine_id?: string | null;
  mine?: string | null;
  /** fork(oem v1.8): one class or a comma list, e.g. `'sdrill'`, `'jumbo,longhole'`. */
  cls?: string | null;
  /** fork(oem v1.8): a register subclass, e.g. `'Top-hammer drills'`. */
  subclass?: string | null;
}

/** Demo line 4014. */
export const predictRisk: OemTool<PredictRiskInput, RiskRow[]> = {
  name: 'predict_risk',
  description:
    'Heuristic failure-risk score (0-1) for one machine or all machines at a mine or in a class, combining flagged component health, telemetry slope, hours since component change, overdue PMs and recent breakdowns. Returns ranked machines with the driving factors. Input {machine_id?} or {mine?, cls?, subclass?}.',
  inputSchema: {
    type: 'object',
    properties: {
      machine_id: { type: 'string' },
      mine: { type: 'string' },
      cls: { type: 'string' },
      subclass: { type: 'string' },
    },
  },
  async execute({ machine_id, mine, cls, subclass } = {}) {
    let fs = FLEET.slice();
    if (machine_id) fs = fs.filter((f) => f.machine_id === upper(machine_id));
    if (mine) fs = fs.filter((f) => f.mine === upper(mine));
    if (cls) {
      const set = classSet(cls);
      fs = fs.filter((f) => set.has(f.cls));
    }
    if (subclass) fs = fs.filter((f) => f.subclass === subclass);

    const [workOrders, tele] = await Promise.all([loadWorkOrders(), loadTelemetry()]);

    return fs
      .map((f): RiskRow => {
        const id = f.machine_id;
        const h = healthOf(id);
        const worst: ComponentHealth = h.slice().sort((a, b) => a.health_score - b.health_score)[0];
        const dm = tele.dailyMetric.filter((d) => d.machine_id === id).map((d) => d.value);
        const sl = (slope(dm) / Math.max(1, Math.abs(dm[0] ?? 0))) * 100;
        const ws = wosOf(id, { workOrders });
        const bd90 = ws.filter((w) => w.wo_type === 'Breakdown' && w.created >= '2026-06-01').length;
        const late = ws.filter((w) => w.status.includes('late')).length;
        const abuse = tele.shiftDaily
          .filter((s) => s.machine_id === id)
          .reduce((a, s) => a + s.abuse, 0);

        let r = 0;
        const fac: string[] = [];
        if (worst.health_score < 20) {
          r += 0.55;
          fac.push(`${worst.component} condition ${worst.health_score} (major issue)`);
        } else if (worst.health_score < 40) {
          r += 0.45;
          fac.push(`${worst.component} condition ${worst.health_score} (very poor)`);
        } else if (worst.health_score < 60) {
          r += 0.25;
          fac.push(`${worst.component} condition ${worst.health_score} (poor)`);
        }
        if (Math.abs(sl) > 0.4) {
          r += 0.2;
          fac.push(`telemetry drifting ${sl > 0 ? '+' : ''}${sl.toFixed(1)}%/day`);
        }
        if (bd90) {
          r += 0.1 * bd90;
          fac.push(`${bd90} breakdown(s) since Jun`);
        }
        if (late) {
          r += 0.05 * Math.min(3, late);
          fac.push(`${late} late PMs`);
        }
        if (abuse > 8) {
          r += 0.1;
          fac.push(`${abuse} abuse events / 12 d`);
        }
        // `status` carries the band in this data, never the demo's sentinel
        // `'ok'`, so the second clause is always true. Kept as written.
        if (worst.hours_since_change > 4000 && (worst.status as string) !== 'ok') {
          r += 0.1;
          fac.push(`${worst.hours_since_change} h since change`);
        }

        return {
          machine: id,
          mine: f.mine,
          model: f.model,
          risk: fixed(Math.min(0.98, r), 2),
          factors: fac,
          recommendation:
            r > 0.6
              ? 'Plan change-out / inspection this week'
              : r > 0.35
                ? 'Watch — add to next PM'
                : 'No action',
        };
      })
      .sort((a, b) => b.risk - a.risk)
      .slice(0, 12);
  },
};

// ===========================================================================
// 11 · part_network — fork(oem v1.8)
// ===========================================================================

/** One location's holding of a part. */
export interface PartLocation {
  location: string;
  qty: number;
  min: number;
  below: boolean;
}

export interface PartNetworkResult extends ToolResult {
  part: string;
  description: string;
  list_price_aud: number;
  lead_time_days: number;
  criticality: string;
  /** Units on hand across every store and DC. */
  on_hand: number;
  /** Locations holding at least one. */
  holding: PartLocation[];
  /** Every stocked location, holding or not. */
  locations: PartLocation[];
  /** The exchange unit in play for this part, if any. */
  exchange: ExchangeUnit | null;
  /** Open or recent orders for the part: `'SO-90431 S-09 — Draft requisition …'`. */
  orders: string[];
}

/** The catalogue part a phrase names best — every word of the phrase scores. */
export function bestPart(parts: readonly CataloguePart[], phrase: string): CataloguePart | null {
  const words = phrase.toLowerCase().match(/[a-z0-9]{3,}/g) ?? [];
  let best: CataloguePart | null = null;
  let top = 0;
  for (const p of parts) {
    const hay = `${p.description} ${p.system}`.toLowerCase();
    const sc = words.filter((w) => hay.includes(w)).length;
    if (sc > top) {
      top = sc;
      best = p;
    }
  }
  return best;
}

/**
 * fork(oem v1.8). Where a part sits across the whole supply network — every
 * site store, DC and service centre — plus the exchange unit and the orders
 * in flight. Answers "is it the only one in the network?".
 */
export const partNetwork: OemTool<
  { part_no?: string | null; keyword?: string | null },
  PartNetworkResult | null
> = {
  name: 'part_network',
  description:
    'Where one part sits across the whole network (every site store, distribution centre and service centre), with minimums, the exchange unit in play and orders in flight. Input {part_no} or {keyword} (e.g. "rotary head motor").',
  inputSchema: {
    type: 'object',
    properties: { part_no: { type: 'string' }, keyword: { type: 'string' } },
  },
  async execute({ part_no, keyword } = {}) {
    const parts = await loadOemDomain('parts');
    const p = part_no
      ? parts.parts.find((x) => x.part_no.toUpperCase() === upper(part_no)) ??
        parts.parts.find((x) => x.part_no.toUpperCase().includes(upper(part_no)))
      : keyword
        ? bestPart(parts.parts, String(keyword))
        : null;
    if (!p) return null;
    const locations = parts.inventory
      .filter((i) => i.part_no === p.part_no)
      .map((i) => ({
        location: i.location,
        qty: i.qty_on_hand,
        min: i.min_level,
        below: i.qty_on_hand < i.min_level,
      }));
    return {
      part: p.part_no,
      description: p.description,
      list_price_aud: p.list_price_aud,
      lead_time_days: p.lead_time_days,
      criticality: p.criticality,
      on_hand: locations.reduce((a, l) => a + l.qty, 0),
      holding: locations.filter((l) => l.qty > 0),
      locations,
      exchange: EXCHANGE.find((x) => x.part_no === p.part_no) ?? null,
      orders: parts.orders
        .filter((o) => o.part_no === p.part_no && !/^Delivered$/.test(o.status))
        .map((o) => `${o.order_no} ${o.machine_id} — ${o.status}`),
    };
  },
};

// ===========================================================================
// 12 · pm_due — fork(oem v1.8)
// ===========================================================================

export interface PmDueRow extends Record<string, unknown> {
  wo: string;
  machine: string;
  mine: MineCode;
  date: string;
  pm_code: string | null;
  description: string;
  labour_h: number;
  crew: string;
}

export interface PmDueResult extends ToolResult {
  days: number;
  mine: MineCode | null;
  count: number;
  labour_h: number;
  rows: PmDueRow[];
}

/** fork(oem v1.8). Preventive work orders scheduled inside a window — `pmDue()` in the engine. */
export const pmDueTool: OemTool<
  { days: number; mine?: string | null; machine_id?: string | null; cls?: string | null },
  PmDueResult
> = {
  name: 'pm_due',
  description:
    'Preventive work orders scheduled inside the next N days (from 09 Sep 2026), optionally for one mine, machine or class: date, machine, PM code, labour hours and crew. Input {days, mine?, machine_id?, cls?}.',
  inputSchema: {
    type: 'object',
    properties: {
      days: { type: 'number' },
      mine: { type: 'string' },
      machine_id: { type: 'string' },
      cls: { type: 'string' },
    },
    required: ['days'],
  },
  async execute({ days, mine, machine_id, cls }) {
    const workOrders = await loadWorkOrders();
    const code = mine ? (upper(mine) as MineCode) : null;
    const set = cls ? classSet(cls) : null;
    const rows = pmDue(days, { workOrders })
      .filter(
        (w) =>
          (!code || w.mine === code) &&
          (!machine_id || w.machine_id === upper(machine_id)) &&
          (!set || set.has(BY_ID[w.machine_id].cls)),
      )
      .sort((a, b) => a.created.localeCompare(b.created))
      .map((w) => ({
        wo: w.wo_id,
        machine: w.machine_id,
        mine: w.mine,
        date: w.created.slice(0, 10),
        pm_code: w.pm_code,
        description: w.description,
        labour_h: w.labour_h,
        crew: w.crew,
      }));
    return {
      days,
      mine: code,
      count: rows.length,
      labour_h: fixed(rows.reduce((a, r) => a + (r.labour_h || 0), 0), 1),
      rows,
    };
  },
};

// ===========================================================================
// 13 · alert_confidence — fork(oem v1.8)
// ===========================================================================

export interface AlertConfidenceResult extends ToolResult {
  alert: string;
  machine: string;
  component: string;
  title: string;
  mode: string;
  raised: string;
  window: string;
  /** 0–100. */
  confidence_pct: number;
  why: string;
  evidence: string[];
  /** The Diagnostic agent's own line in the chain. */
  diagnostic: string | null;
  planned: number | null;
  unplanned: number | null;
}

/**
 * fork(oem v1.8). How far to trust one prediction: the alert's model
 * confidence, the evidence it fused, the diagnostic chain step and the
 * planned / unplanned price of acting or not.
 */
export const alertConfidence: OemTool<
  { machine_id?: string | null; alert_id?: string | null },
  AlertConfidenceResult | null
> = {
  name: 'alert_confidence',
  description:
    'The open alert on a machine (or by id): model confidence %, failure window, live vs shift-end mode, the reasoning, every piece of evidence it fused, the Diagnostic agent step, and the planned vs unplanned cost. Input {machine_id} or {alert_id}.',
  inputSchema: {
    type: 'object',
    properties: { machine_id: { type: 'string' }, alert_id: { type: 'string' } },
  },
  async execute({ machine_id, alert_id } = {}) {
    const a = alert_id
      ? ALERTS.find((x) => x.id === upper(alert_id))
      : ALERTS.find((x) => x.machine === upper(machine_id));
    if (!a) return null;
    const c = coi(a);
    return {
      alert: a.id,
      machine: a.machine,
      component: a.comp,
      title: a.title,
      mode: a.mode,
      raised: a.raised,
      window: a.window,
      confidence_pct: Math.round(a.conf * 100),
      why: a.why,
      evidence: a.evidence.map((e) => `${e.source}: ${e.detail}`),
      diagnostic: a.chain.find((s) => s.agent === 'Diagnostic')?.output ?? null,
      planned: c ? Math.round(c.planned) : null,
      unplanned: c ? Math.round(c.unplanned) : null,
    };
  },
};

// ===========================================================================
// 14 · job_cost — fork(oem v1.8)
// ===========================================================================

export interface JobCostResult extends ToolResult {
  machine: string;
  mine: MineCode;
  component: string;
  condition: number | null;
  hours_since_change: number | null;
  /** Where `planned` came from. */
  basis: 'alert' | 'draft' | 'estimate';
  planned: number;
  /** Same failure unplanned, when an alert prices it. */
  unplanned: number | null;
  downtime_h_unplanned: number | null;
  lost_rate: number;
  labour_rate: number;
  plan_note: string | null;
  failure_note: string | null;
  alert: string | null;
  window: string | null;
  confidence_pct: number | null;
  draft: {
    wo: string;
    direct: number;
    labour_h: number;
    parts: string | null;
    crew: string;
  } | null;
  parts: { part: string; description: string; list_price_aud: number; lead_time_days: number }[];
  exchange: ExchangeUnit | null;
  orders: string[];
}

/**
 * fork(oem v1.8). What a planned job on one machine's component costs, and
 * what the same failure costs if it happens first. Prefers the alert's priced
 * planned/unplanned pair (`coi()`), then the agent-drafted work order, then an
 * estimate: catalogue parts plus the fleet's mean labour hours on that
 * component at the site labour rate.
 */
export const jobCost: OemTool<{ machine_id: string; component?: string | null }, JobCostResult> = {
  name: 'job_cost',
  description:
    'Planned cost of a job on one machine (optionally one component): the priced planned vs unplanned pair when an alert covers it, else the drafted work order, else an estimate from catalogue parts and mean labour hours; plus parts, exchange unit and orders in flight. Input {machine_id, component?}.',
  inputSchema: {
    type: 'object',
    properties: { machine_id: { type: 'string' }, component: { type: 'string' } },
    required: ['machine_id'],
  },
  async execute({ machine_id, component }) {
    const id = requireMachine(machine_id);
    const f = BY_ID[id];
    const [workOrders, parts] = await Promise.all([loadWorkOrders(), loadOemDomain('parts')]);
    const health = healthOf(id);
    const want = component ? String(component).toLowerCase() : null;
    const alert = ALERTS.find(
      (a) => a.machine === id && (!want || a.comp.toLowerCase().includes(want) || want.includes(a.comp.toLowerCase())),
    );
    const drafts = workOrders.filter((w) => w.machine_id === id && w.status.startsWith('Draft'));
    const draft =
      drafts.find((w) => !want || w.component.toLowerCase().includes(want)) ??
      (want ? undefined : drafts[0]);
    const worst = health.slice().sort((a, b) => a.health_score - b.health_score)[0];
    const comp =
      (want && health.find((h) => h.component.toLowerCase().includes(want))?.component) ||
      alert?.comp ||
      draft?.component ||
      worst.component;
    const h = health.find((x) => x.component === comp);
    const partNos = PART_OF_COMP.find(([re]) => re.test(comp))?.[1] ?? [];
    const catalogue = partNos
      .map((pn) => parts.parts.find((p) => p.part_no === pn))
      .filter((p): p is CataloguePart => p != null && (p.fits === 'all' || p.fits === f.cls));
    const rate = RATES[f.mine].labour;
    const c = alert ? coi(alert) : null;
    let basis: JobCostResult['basis'];
    let planned: number;
    if (c) {
      basis = 'alert';
      planned = c.planned;
    } else if (draft) {
      basis = 'draft';
      planned = draft.total_direct_cost_aud;
    } else {
      basis = 'estimate';
      const past = workOrders.filter(
        (w) => w.component === comp && w.wo_type !== 'Preventive' && w.labour_h > 0,
      );
      const labourH = past.length ? past.reduce((a, w) => a + w.labour_h, 0) / past.length : 4;
      planned = fixed(labourH, 1) * rate + (catalogue[0]?.list_price_aud ?? 0);
    }
    const lostRate = RATES[f.mine].lost[f.cls] ?? RATES[f.mine].lost.lhd ?? 1000;
    return {
      machine: id,
      mine: f.mine,
      component: comp,
      condition: h?.health_score ?? null,
      hours_since_change: h?.hours_since_change ?? null,
      basis,
      planned: Math.round(planned),
      unplanned: c ? Math.round(c.unplanned) : null,
      downtime_h_unplanned: c ? c.hU : null,
      lost_rate: lostRate,
      labour_rate: rate,
      plan_note: c?.pl ?? null,
      failure_note: c?.sec ?? null,
      alert: alert?.id ?? null,
      window: alert?.window ?? null,
      confidence_pct: alert ? Math.round(alert.conf * 100) : null,
      draft: draft
        ? {
            wo: draft.wo_id,
            direct: draft.total_direct_cost_aud,
            labour_h: draft.labour_h,
            parts: draft.parts_used,
            crew: draft.crew,
          }
        : null,
      parts: catalogue.map((p) => ({
        part: p.part_no,
        description: p.description,
        list_price_aud: p.list_price_aud,
        lead_time_days: p.lead_time_days,
      })),
      exchange: EXCHANGE.find((x) => partNos.includes(x.part_no) && x.where.startsWith(id)) ?? null,
      orders: parts.orders
        .filter((o) => o.machine_id === id && partNos.includes(o.part_no) && o.status !== 'Delivered')
        .map((o) => `${o.order_no} — ${o.status}`),
    };
  },
};

// ===========================================================================
// Registry
// ===========================================================================

/**
 * The ten demo tools, in the demo's declaration order — which is also the
 * order the live-mode prompt lists them in — then the four fork(oem v1.8)
 * additions the router's intents call.
 */
export const TOOLS = {
  fleet_overview: fleetOverview,
  machine_history: machineHistory,
  breakdown_trend: breakdownTrend,
  fault_trend: faultTrend,
  cost_summary: costSummary,
  stock_check: stockCheck,
  energy_summary: energySummary,
  generate_report: generateReport,
  search_sources: searchSources,
  predict_risk: predictRisk,
  part_network: partNetwork,
  pm_due: pmDueTool,
  alert_confidence: alertConfidence,
  job_cost: jobCost,
} as const;

/** Tool names, for the "Ran … → …" line and the Tier 3 live-mode registration. */
export type OemToolName = keyof typeof TOOLS;

/** Ordered `{name, description, inputSchema}` triples — the demo's `toolList`. */
export const TOOL_MANIFEST: readonly {
  name: string;
  description: string;
  inputSchema?: ToolInputSchema;
}[] = Object.values(TOOLS).map((t) => ({
  name: t.name,
  description: t.description,
  inputSchema: t.inputSchema,
}));

/** Sort weight for the band tables the tools emit. Re-exported for consumers. */
export { CONDITION_BAND_ORDER };
